import React from 'react';
import { 
  Typography, 
  Grid, 
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Button
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const BlogPage: React.FC = () => {
  const blogPosts = [
    {
      title: "5 Reasons Why You Need a Temporary Email Address",
      excerpt: "Protect your privacy and avoid spam with temporary email addresses. Learn how they can enhance your online security.",
      author: "1TimeMail Team",
      date: "2024-01-15",
      category: "Privacy",
      readTime: "5 min read"
    },
    {
      title: "The Ultimate Guide to Email Privacy in 2024",
      excerpt: "Complete guide to protecting your email privacy, from temporary addresses to encrypted communication.",
      author: "Security Expert",
      date: "2024-01-10",
      category: "Security",
      readTime: "8 min read"
    },
    {
      title: "How Temporary Email Addresses Work",
      excerpt: "Deep dive into the technology behind temporary email services and how they protect your identity.",
      author: "Tech Team",
      date: "2024-01-05",
      category: "Technology",
      readTime: "6 min read"
    },
    {
      title: "Best Practices for Online Account Registration",
      excerpt: "Learn how to safely register for online services without exposing your personal email address.",
      author: "Privacy Advocate",
      date: "2023-12-28",
      category: "Tips",
      readTime: "4 min read"
    },
    {
      title: "Understanding Email Tracking and How to Avoid It",
      excerpt: "Learn about email tracking pixels and other methods companies use to monitor your email activity.",
      author: "1TimeMail Team",
      date: "2023-12-20",
      category: "Privacy",
      readTime: "7 min read"
    },
    {
      title: "The Rise of Disposable Email Services",
      excerpt: "Explore the growing trend of temporary email services and their impact on digital privacy.",
      author: "Industry Analyst",
      date: "2023-12-15",
      category: "Industry",
      readTime: "5 min read"
    }
  ];

  const categories = ["All", "Privacy", "Security", "Technology", "Tips", "Industry"];
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <PageTemplate 
      title="Blog" 
      description="Stay updated with the latest insights on email privacy, security, and digital protection."
      breadcrumbs={[{ label: 'Blog', path: '/blog' }]}
    >
      {/* Category Filter */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Stack 
          direction="row" 
          spacing={1} 
          justifyContent="center" 
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              variant={selectedCategory === category ? "filled" : "outlined"}
              color={selectedCategory === category ? "primary" : "default"}
              onClick={() => setSelectedCategory(category)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme => selectedCategory === category 
                    ? 'primary.main' 
                    : theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Blog Posts Grid */}
      <Grid container spacing={4}>
        {filteredPosts.map((post, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={post.category} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                    {post.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {post.excerpt}
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 'auto' }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {post.author}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {post.readTime}
                      </Typography>
                    </Stack>
                  </Stack>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                  
                  <Button 
                    variant="text" 
                    color="primary" 
                    size="small"
                    sx={{ textTransform: 'none', p: 0, justifyContent: 'flex-start' }}
                  >
                    Read more →
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Coming Soon Message */}
      <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'action.hover', borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          More Articles Coming Soon!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We're working on bringing you more valuable content about email privacy and security.
        </Typography>
      </Box>
    </PageTemplate>
  );
};

export default BlogPage;